import React from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, ArrowRight, X } from "lucide-react";
import { useBilling } from "../context/BillingContext";
import { isNativeApp } from "../lib/platform";

/**
 * Compact banner shown on the Dashboard reminding the user about their
 * trial state. Shows up when:
 *   - in trial AND days_remaining <= 14 (urgency)
 *   - trial has ended (status === 'free')
 *
 * Hidden completely for active Pro users.
 */
export default function TrialBanner() {
  const { sub } = useBilling();
  const [dismissed, setDismissed] = React.useState(false);

  if (!sub || dismissed) return null;
  if (sub.status === "active") return null;

  const inTrial = sub.in_trial;
  const ended = sub.status === "free";
  const days = sub.days_remaining;

  // Only show during last 14 days of trial OR when trial ended.
  if (inTrial && days > 14) return null;

  const isUrgent = ended || days <= 3;
  const native = isNativeApp();

  return (
    <div
      className={`mb-6 border ${
        isUrgent ? "border-[#FFB020]/40 bg-[#FFB020]/5" : "border-[#6B9BFF]/30 bg-[#6B9BFF]/5"
      } px-5 py-4 flex flex-wrap items-center gap-4`}
      data-testid="trial-banner"
    >
      <div className={`w-9 h-9 flex items-center justify-center border shrink-0 ${
        isUrgent ? "border-[#FFB020]/40 text-[#FFB020]" : "border-[#6B9BFF]/40 text-[#6B9BFF]"
      }`}>
        <Crown className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow text-white/50 mb-0.5 text-[10px]">
          {ended ? "Free plan" : "Free trial"}
        </div>
        <div className="text-sm font-bold text-white">
          {ended ? (
            "Your free trial has ended."
          ) : days === 0 ? (
            "Your trial ends today."
          ) : days === 1 ? (
            "1 day left of your free trial."
          ) : (
            `${days} days left of your free Pro trial.`
          )}
        </div>
        <div className="text-xs text-white/60 mt-0.5">
          {native ? (
            ended
              ? "Sign in on the web at DAIONE to upgrade."
              : "After the trial, Pro is managed on the web."
          ) : (
            ended
              ? "Upgrade to $9.99 AUD/month to unlock AI workouts and meal photo logging again."
              : "After the trial, Pro is $9.99 AUD/month. Cancel anytime."
          )}
        </div>
      </div>
      {!native && (
        <Link
          to="/app/billing"
          className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-[10px] px-4 py-2.5 hover:bg-white inline-flex items-center gap-1.5 shrink-0"
          data-testid="trial-banner-upgrade"
        >
          <Sparkles className="w-3 h-3" /> Upgrade <ArrowRight className="w-3 h-3" />
        </Link>
      )}
      <button
        onClick={() => setDismissed(true)}
        className="text-white/30 hover:text-white shrink-0"
        data-testid="trial-banner-dismiss"
        title="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
